import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { choiceDisplayText, formatCount, layoutLabel, shortText } from './fanScanUtils';

function ChoiceTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <Paper sx={{ p: 1.25, maxWidth: 320 }}>
      <Typography variant="body2">{choiceDisplayText(row)}</Typography>
      <Typography variant="caption" color="text.secondary">
        {formatCount(row.count)} answers
      </Typography>
    </Paper>
  );
}

function FanScanAnalytics({ analytics }) {
  const questions = analytics?.questions || [];

  if (!questions.length) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>No question breakdown for this range.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Counts include sessions still in progress. Preference questions list both sides of each pair.
      </Typography>
      {questions.map((question) => {
        const choices = (question.choices || []).map((choice) => ({
          ...choice,
          label: shortText(choiceDisplayText(choice), 42),
          display: choiceDisplayText(choice),
        }));
        return (
          <Paper key={question.question_id} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
              <Typography variant="h6">{question.title}</Typography>
              <Chip size="small" label={question.question_id} />
              <Chip size="small" variant="outlined" label={layoutLabel(question.layout_kind)} />
            </Box>
            <Box sx={{ width: '100%', height: Math.max(180, choices.length * 42) }}>
              <ResponsiveContainer>
                <BarChart data={choices} layout="vertical" margin={{ left: 8, right: 36 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="label" width={220} tick={{ fontSize: 12 }} />
                  <Tooltip content={<ChoiceTooltip />} />
                  <Bar dataKey="count" fill="#0f766e" name="Answers">
                    <LabelList dataKey="count" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {choices.map((choice) => (
                <Typography component="li" variant="body2" key={choice.id} sx={{ mb: 0.5 }}>
                  {choice.display}{' '}
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    ({formatCount(choice.count)})
                  </Box>
                </Typography>
              ))}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}

export default FanScanAnalytics;
